import { useEffect, useMemo, useRef, useState } from "react"
import { useExamApi } from "../context"
import { presets } from "../themePresets"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronLeft, ChevronRight, CheckCircle2, TimerReset, Send, Palette, Loader2, Shield, CheckCircle2 as CC2 } from "lucide-react"
import ConfirmModal from "@/widgets/ConfirmModal"
import { examTypeMapping, typeSummaryMapping } from "@/settings/master"
import { inferQuestionPresentation } from "@/widgets/questions/utils"

const fade = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 12 }, transition: { duration: 0.25 } }

function extractDescription(txt) {
    if (txt == null) return ""
    
    const getVal = (obj) => {
        if (!obj || typeof obj !== "object") return null
        const d = obj.description ?? obj.desc ?? obj.text ?? obj.title ?? obj.content ?? obj.label ?? obj.name ?? ""
        if (typeof d === "string" && d.trim() !== "") return d
        const firstStr = Object.values(obj).find(v => typeof v === "string" && v.trim() !== "")
        if (firstStr) return firstStr
        return typeof d === "string" ? d : ""
    }

    if (typeof txt === "object") {
        const val = getVal(txt)
        if (val) return val
        return JSON.stringify(txt)
    }
    
    const s = String(txt)
    const m = s.match(/["'](?:description|desc|text|title|content|label|name)["']\s*:\s*["']([\s\S]*?)["']/)
    if (m && m[1]) return m[1]
    
    try {
        const fixed = s.replace(/([{,\s])'([^']+?)'\s*:/g, '$1"$2":').replace(/:\s*'([^']*?)'/g, ':"$1"')
        const obj = JSON.parse(fixed)
        const k = Object.keys(obj)[0]
        const inner = (typeof obj[k] === "object" && obj[k]) ? obj[k] : obj
        
        const val = getVal(inner)
        if (val) return val
        return typeof inner === "string" ? inner : s
    } catch {
        return s
    }
}

export default function ExamPlay({ onResult }) {
    const { api, theme, setTheme, t } = useExamApi()
    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState("")
    const [data, setData] = useState(null)
    const [idx, setIdx] = useState(0)
    const [sel, setSel] = useState({})
    const [remain, setRemain] = useState(0)
    const [spend, setSpend] = useState(0)
    const [confirm, setConfirm] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [themeOpen, setThemeOpen] = useState(false)
    const [collapsed, setCollapsed] = useState(sessionStorage.getItem("kuizy_theme_collapsed") === "1")
    const timerRef = useRef(null)
    const nick = useMemo(() => sessionStorage.getItem("player_nick") || "Member", [])
    const applyTheme = v => { setTheme(v); localStorage.setItem("kuizy_theme", v); setThemeOpen(false) }
    const toggleCollapse = () => { const v = !collapsed; setCollapsed(v); sessionStorage.setItem("kuizy_theme_collapsed", v ? "1" : "0"); if (v) setThemeOpen(false) }

    useEffect(() => {
        console.log("[ExamPlay] api.kind =", api.kind)
        setLoading(true)
        setErr("")
        api.getQuestions()
            .then(d => {
                setData(d)
                const sec = Math.max(0, Number(d?.test?.duration || 0)) * 60
                setRemain(sec)
                setLoading(false)
            })
            .catch(() => { setErr("ไม่สามารถเริ่มทำข้อสอบได้"); setLoading(false) })
    }, [api])

    useEffect(() => {
        if (!data) return
        if ((data?.test?.duration || 0) <= 0) return
        if (timerRef.current) clearInterval(timerRef.current)
        timerRef.current = setInterval(() => {
            setRemain(v => {
                if (v <= 1) {
                    clearInterval(timerRef.current)
                    handleSubmit(true)
                    return 0
                }
                return v - 1
            })
            setSpend(s => s + 1)
        }, 1000)
        return () => { if (timerRef.current) clearInterval(timerRef.current) }
    }, [data])

    if (loading) return <div className={`min-h-screen grid place-items-center ${t.bg} ${t.text}`}>กำลังโหลด...</div>
    if (err) return <div className={`min-h-screen grid place-items-center ${t.bg} text-danger`}>{err}</div>
    if (!data) return null

    const qs = data.questions || []
    const q = qs[idx] || null
    const total = qs.length
    const present = inferQuestionPresentation(q)
    const selected = sel[q?.id] ?? (present.kind === "multi" ? [] : present.kind === "weighted" ? {} : "")

    const toggle = k => {
        if (!q) return
        if (present.kind === "multi") {
            const arr = Array.isArray(selected) ? selected.slice() : []
            const i = arr.indexOf(k)
            if (i >= 0) arr.splice(i, 1); else arr.push(k)
            setSel(p => ({ ...p, [q.id]: arr }))
        } else if (present.kind === "single") {
            setSel(p => ({ ...p, [q.id]: k }))
        }
    }

    const next = () => setIdx(i => Math.min(total - 1, i + 1))
    const prev = () => setIdx(i => Math.max(0, i - 1))
    const jump = i => setIdx(i)

    const fmt = s => {
        const mm = Math.floor(s / 60).toString().padStart(2, "0")
        const ss = (s % 60).toString().padStart(2, "0")
        return `${mm}:${ss}`
    }

    const handleSubmit = auto => {
        if (submitting) return
        setSubmitting(true)
        const base = { answers: sel, spend_sec: spend }
        const isPublic = api.kind === "public"
        const payload = isPublic ? { ...base, nick: nick } : base
        api.submit(payload)
            .then(r => {
                const rid = api.getRunId()
                const sid = r?.submission_id || r?.id
                const score = r?.score || 0
                const full = r?.full_score || 0
                onResult({ runId: rid, submissionId: sid, score, full, spend })
            })
            .catch(() => { setSubmitting(false) })
    }

    const progress = Math.round((Object.keys(sel).length / Math.max(1, total)) * 100)
    const badgeType = examTypeMapping[present.examTypeKey] || present.examTypeKey
    const badgeSummary = typeSummaryMapping[present.summaryKey] || present.summaryKey

    return (
        <div className={`min-h-screen flex flex-col p-4 md:p-6 ${t.bg}`}>
            <div className="fixed top-3 right-3 z-50">
                {collapsed ? (
                    <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
                        <ChevronLeft className="size-5" />
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
                            <ChevronRight className="size-5" />
                        </button>
                        <div className="relative">
                            <button onClick={() => setThemeOpen(s => !s)} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
                                <Palette className="size-5" />
                            </button>
                            {themeOpen && (
                                <div className={`absolute right-0 mt-2 w-44 rounded-xl p-2 backdrop-blur ${t.card} ${t.menuText}`}>
                                    <div className={`text-xs mb-2 px-1 opacity-80 ${t.subtextStrong}`}>ธีม</div>
                                    {Object.keys(presets).map(k => (
                                        <button key={k} onClick={() => applyTheme(k)} className={`w-full h-9 px-3 rounded-lg flex items-center justify-between ${t.soft}`}>
                                            <span>{k}</span>{theme === k ? <CC2 className="size-4 text-primary" /> : null}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="max-w-6xl w-full mx-auto flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-3">
                    <div className={`text-lg ${t.text}`}>{data.test?.name || "Exam"}{api.kind === "public" && data.preview ? " (โหมดทดสอบ)" : ""}</div>
                    <div className="flex items-center gap-2">
                        <div className={`rounded-xl px-3 h-10 flex items-center ${t.text} ${t.soft}`}>{fmt(remain)}</div>
                        <div className="w-8"></div>
                        {/* <button onClick={() => setConfirm(true)} disabled={submitting} className={`h-10 rounded-xl px-4 ${submitting ? "bg-primary/60" : "bg-primary"} text-white flex items-center gap-2`}>
                            {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}ส่งคำตอบ
                        </button> */}
                    </div>
                </div>

                <div className={`mt-1 text-xs ${t.subtext}`}>
                    {badgeType ? <>ประเภทคำถาม: <span className={t.text}>{badgeType}</span> • <span className={t.text}>{badgeSummary}</span></> : null}
                </div>

                <div className={`mt-4 h-2 rounded-full overflow-hidden ${t.track}`}>
                    <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
                    <div className="lg:col-span-2 h-full">
                        <AnimatePresence mode="wait">
                            <motion.div key={q?.id} {...fade} className={`h-full rounded-2xl p-6 flex flex-col ${t.card}`}>
                                <div className={`text-sm mb-1 ${t.subtext}`}>ข้อ {idx + 1}/{total}</div>
                                <div className={`${t.text} text-lg leading-relaxed flex-1 overflow-auto`}>{q?.question}</div>
                                {present.kind !== "weighted" ? (
                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {q?.options?.map((o, i) => {
                                            const active = present.kind === "multi"
                                                ? (Array.isArray(selected) ? selected : []).includes(o.key)
                                                : selected === o.key
                                            const label = extractDescription(o.text ?? o.title ?? o.content ?? o.label ?? o.desc ?? o)
                                            return (
                                                <button key={o.key} onClick={() => toggle(o.key)} disabled={submitting} className={`w-full text-left rounded-xl border px-4 py-3 ${active ? `bg-primary/20 border-primary ${theme === "dark" ? "text-white" : ""}` : `${t.soft} ${t.softBorder} ${t.text}`} ${submitting ? "opacity-60 pointer-events-none" : ""}`}>
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-start gap-3">
                                                            <div className={`min-w-7 h-7 rounded-lg grid place-items-center mt-0.5 ${active ? "bg-primary text-white" : `${t.soft} ${t.softText}`}`}>{i + 1}</div>
                                                            <div className="text-sm leading-relaxed">{label}</div>
                                                        </div>
                                                        {active ? <CheckCircle2 className="size-5 text-primary shrink-0" /> : null}
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <div className="mt-5 grid grid-cols-1 gap-3">
                                        {q?.options?.map(o => {
                                            const label = extractDescription(o.text ?? o.title ?? o.content ?? o.label ?? o.desc ?? o)
                                            const min = Number.isFinite(q?.weightMin) ? q.weightMin : 0
                                            const max = Number.isFinite(q?.weightMax) ? q.weightMax : 5
                                            const base = (selected && typeof selected === "object") ? selected : {}
                                            const current = Number.isFinite(base[o.key]) ? base[o.key] : 0
                                            const setWeight = val => {
                                                const num = Math.max(min, Math.min(max, Number(val) || 0))
                                                const next = { ...base, [o.key]: num }
                                                setSel(p => ({ ...p, [q.id]: next }))
                                            }
                                            return (
                                                <div key={o.key} className={`rounded-xl border p-3 ${t.soft} ${t.softBorder}`}>
                                                    <div className={`text-sm mb-2 ${t.text}`}>{label}</div>
                                                    <div className="flex items-center gap-3">
                                                        <input type="range" min={min} max={max} value={current} onChange={e => setWeight(e.target.value)} disabled={submitting} className="flex-1 accent-primary" />
                                                        <input type="number" min={min} max={max} value={current} onChange={e => setWeight(e.target.value)} disabled={submitting} className={`w-16 h-9 rounded-lg border text-center ${t.inputAltBg} ${t.inputAltText} ${t.inputAltBorder}`} />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                <div className="mt-6 flex items-center justify-between">
                                    <button onClick={prev} disabled={idx === 0 || submitting} className={`h-10 px-4 rounded-xl disabled:opacity-40 flex items-center gap-2 ${t.neutralBtn}`}>
                                        <ChevronLeft className="size-4" /> ก่อนหน้า
                                    </button>
                                    <div className={`text-xs ${t.subtext}`}>ตอบแล้ว {Object.keys(sel).length}/{total} ข้อ</div>
                                    <button onClick={next} disabled={idx === total - 1 || submitting} className={`h-10 px-4 rounded-xl disabled:opacity-40 flex items-center gap-2 ${t.neutralBtn}`}>
                                        ถัดไป <ChevronRight className="size-4" />
                                    </button>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="lg:col-span-1">
                        <div className={`rounded-2xl p-4 h-full flex flex-col ${t.card}`}>
                            <div className={`text-sm mb-2 ${t.subtext}`}>ไปยังข้อ</div>
                            <div className="grid grid-cols-5 gap-2">
                                {qs.map((qq, i) => {
                                    const done = sel[qq.id] !== undefined
                                    const active = i === idx
                                    return (
                                        <button key={qq.id} onClick={() => jump(i)} disabled={submitting} className={`h-9 rounded-lg text-sm ${active ? "bg-primary text-white" : done ? "bg-green-500/20 text-green-700" : `${t.soft} ${t.softText}`} ${submitting ? "opacity-60 pointer-events-none" : ""}`}>
                                            {i + 1}
                                        </button>
                                    )
                                })}
                            </div>

                            {api.kind === "public" && (
                                <div className={`mt-4 text-xs ${t.subtext}`}>
                                    ชื่อเล่น: <span className={t.text}>{nick}</span>
                                </div>
                            )}

                            <button onClick={() => setConfirm(true)} disabled={submitting} className={`mt-3 w-full h-10 rounded-xl ${submitting ? "bg-primary/60" : "bg-primary"} text-white flex items-center justify-center gap-2`}>
                                {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} ส่งคำตอบ
                            </button>
                            <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-1">
                                <TimerReset className="size-3" /> ใช้เวลา {Math.max(0, spend)} วินาที
                            </div>

                            {data?.preview && api.kind === "public" ? (
                                <div className="mt-3 rounded-lg px-3 py-2 text-xs border border-amber-400/40 bg-amber-500/10 text-amber-700 inline-flex items-center gap-2">
                                    <Shield className="size-4" /> โหมดทดสอบ
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>




            {confirm && (
                <ConfirmModal
                    type="confirm"
                    open={confirm}
                    title="ยืนยันส่งคำตอบ"
                    message="เมื่อส่งแล้วจะไม่สามารถแก้ไขคำตอบได้"
                    onConfirm={() => { setConfirm(false); handleSubmit(false) }}
                    onCancel={() => setConfirm(false)}
                />
            )}

            {submitting && (
                <div className="fixed inset-0 z-[60] grid place-items-center bg-black/40">
                    <div className="px-6 py-4 rounded-2xl bg-white text-gray-900 flex items-center gap-3 shadow-xl">
                        <Loader2 className="size-5 animate-spin" />
                        <div className="text-sm">กำลังส่งคำตอบ...</div>
                    </div>
                </div>
            )}
        </div>
    )
}
